import { TableComponent } from '@innovamat/glimmer-components';
import type { AdminDashboardQuery } from '@innovamat/glow-api-client';
import { useAdminDashboardQuery } from '@innovamat/glow-api-client';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ERROR_TYPE, ErrorPage } from '../../../navigation';
import ReportsLoader from '../../components/reports-loader';
import type { DashboardProps } from '../admin-dashboard';
import { getAdminColumns } from '../admin-dashboard/utils/get-admin-columns';
import { getRowSpan } from '../admin-dashboard/utils/preprocess';
import { useFlag } from '@innovamat/flags';
import { mockDataPE, mockDataEY, mockDataSE } from './mock-data';

type AdminDashboardTableContentProps = {
  data: AdminDashboardQuery | undefined;
  goToClassroom: ((id: string) => void) | undefined;
  goToTestsAndResults: ((id: string) => void) | undefined;
  noDataFilter: boolean | undefined;
  setAllRowsFilledOrEmpty: (value: boolean | undefined) => void;
};

export const AdminDashboardTableContent = ({
  data,
  goToClassroom,
  goToTestsAndResults,
  noDataFilter,
  setAllRowsFilledOrEmpty,
}: AdminDashboardTableContentProps): JSX.Element => {
  const someHaveAssessmentPermissions = useMemo(() => {
    return data?.adminDashboard?.some((row) => row?.tests.hasPermission);
  }, [data]);

  const { t } = useTranslation();
  const columns = getAdminColumns({
    t,
    goToClassroom,
    goToTestsAndResults,
    someHaveAssessmentPermissions,
  });

  const rows = useMemo(() => {
    return noDataFilter
      ? data?.adminDashboard?.filter(
          (row) => row?.digitalPractice?.avgScore !== null
        )
      : data?.adminDashboard;
  }, [data, noDataFilter]);

  useEffect(() => {
    if (!data) setAllRowsFilledOrEmpty(undefined);
    if (data) {
      const allFilled = data?.adminDashboard?.every(
        (row) => row?.digitalPractice?.avgScore !== null
      );
      const allEmpty = data?.adminDashboard?.every(
        (row) => !row?.digitalPractice?.avgScore
      );

      setAllRowsFilledOrEmpty(allFilled || allEmpty);
    }
  }, [setAllRowsFilledOrEmpty, data]);

  return (
    <TableComponent
      id={'ADRE_EARYYEARS'}
      columns={columns}
      rows={data?.adminDashboard ? getRowSpan(rows) : []}
      topStickyOffset={64}
    />
  );
};

const mockDataByStage: Record<string, AdminDashboardQuery> = {
  PE: mockDataPE,
  SE: mockDataSE,
  EY: mockDataEY,
};

export const AdminDashboardTable = ({
  organizationId,
  tabsAreLoaded,
  goToClassroom,
  goToTestsAndResults,
  noDataFilter,
  stage,
  setAllRowsFilledOrEmpty,
}: DashboardProps): JSX.Element => {
  const { value: useMockData, loading: isFlagLoading } = useFlag(
    'schoolDemoMockReports',
    false,
    {
      identifier: '',
      custom: {
        schoolId: organizationId || '',
      },
    }
  );

  const shouldFetchRealData = !isFlagLoading && !useMockData;

  const { data, isLoading, isError } = useAdminDashboardQuery(
    {
      organizationId: organizationId!,
      stage: stage,
    },
    {
      enabled: !!organizationId && shouldFetchRealData,
    }
  );

  const dashboardData =
    !isFlagLoading && useMockData ? mockDataByStage[stage] || mockDataEY : data;

  if (!useMockData && isError) {
    return <ErrorPage errorType={ERROR_TYPE.NOT_AVAILABLE} />;
  }

  if (
    isFlagLoading ||
    (isLoading && !useMockData) ||
    !tabsAreLoaded ||
    !dashboardData
  ) {
    return <ReportsLoader />;
  }

  return (
    <AdminDashboardTableContent
      data={dashboardData}
      goToClassroom={goToClassroom}
      goToTestsAndResults={goToTestsAndResults}
      noDataFilter={noDataFilter}
      setAllRowsFilledOrEmpty={setAllRowsFilledOrEmpty}
    />
  );
};
